import type {ContentBlocksFragment} from '@/modules/foundation/sanity/content-blocks/query';
import {internalLinkToHref} from '@/modules/foundation/sanity/link/query';
import type {KeyedArray} from '@/modules/foundation/shared/types';

import {DOCUMENT_TYPE_BANK_AREA} from '../../../bank/area/sanity/constants';
import {DOCUMENT_TYPE_BANK_FRONTPAGE} from '../../../bank/frontpage/sanity/constants';
import {DOCUMENT_TYPE_COLLECTION_AREA} from '../../../collection/area/sanity/constants';
import {DOCUMENT_TYPE_COLLECTION_FRONTPAGE} from '../../../collection/frontpage/sanity/constants';
import {DOCUMENT_TYPE_INSURANCE_AREA} from '../../../insurance/area/sanity/constants';
import {DOCUMENT_TYPE_INSURANCE_FRONTPAGE} from '../../../insurance/frontpage/sanity/constants';
import type {FieldOfAreaDetails} from '../../components/field-of-area-list';

import type {BLOCK_TYPE_FIELD_OF_AREA_LIST_ALL} from './constants';

export const FIELD_OF_AREA_LIST_ALL_FRAGMENT = `//groq
	...,
	"insurance": *[_type == "${DOCUMENT_TYPE_INSURANCE_FRONTPAGE}"][0] {
		_id,
		_type,
		title,
		"slug": slug.current,
		"areas": *[_type == "${DOCUMENT_TYPE_INSURANCE_AREA}"]|order(orderRank) {
			_id,
			_type,
			title,
			"slug": slug.current,
		}[0...3],
		"areasHasMore": count(*[_type == "${DOCUMENT_TYPE_INSURANCE_AREA}"]) > 3,
		icon
	},
	"bank": *[_type == "${DOCUMENT_TYPE_BANK_FRONTPAGE}"][0] {
		_id,
		_type,
		title,
		"slug": slug.current,
		"areas": *[_type == "${DOCUMENT_TYPE_BANK_AREA}"]|order(orderRank) {
			_id,
			_type,
			title,
			"slug": slug.current,
		}[0...3],
		"areasHasMore": count(*[_type == "${DOCUMENT_TYPE_BANK_AREA}"]) > 3,
		icon
	},
	"collection": *[_type == "${DOCUMENT_TYPE_COLLECTION_FRONTPAGE}"][0] {
		_id,
		_type,
		title,
		"slug": slug.current,
		"areas": *[_type == "${DOCUMENT_TYPE_COLLECTION_AREA}"]|order(orderRank) {
			_id,
			_type,
			title,
			"slug": slug.current,
		}[0...3],
		"areasHasMore": count(*[_type == "${DOCUMENT_TYPE_COLLECTION_AREA}"]) > 3,
		icon
	}
`;

export type FieldOfAreaListAllFragment = Extract<
	ContentBlocksFragment[number],
	{_type: typeof BLOCK_TYPE_FIELD_OF_AREA_LIST_ALL}
>;

export const fieldOfAreaListFromFieldOfAreaListAllSection = (
	section: FieldOfAreaListAllFragment,
): KeyedArray<FieldOfAreaDetails> => {
	const fieldOfAreas: KeyedArray<FieldOfAreaDetails> = [];

	if (!section.bank || !section.collection || !section.insurance) {
		return fieldOfAreas;
	}

	fieldOfAreas.push({
		_key: section.insurance._id,
		title: section.insurance.title ?? '',
		href: internalLinkToHref(section.insurance._type, section.insurance.slug ?? ''),
		areas: section.insurance.areas?.map((area) => ({
			_key: area._id,
			title: area.title ?? '',
			href: internalLinkToHref(area._type, area.slug ?? ''),
		})),
		areasHasMore: section.insurance.areasHasMore,
		icon: section.insurance.icon ?? undefined,
	});

	fieldOfAreas.push({
		_key: section.bank._id,
		title: section.bank.title ?? '',
		href: internalLinkToHref(section.bank._type, section.bank.slug ?? ''),
		areas: section.bank.areas?.map((area) => ({
			_key: area._id,
			title: area.title ?? '',
			href: internalLinkToHref(area._type, area.slug ?? ''),
		})),
		areasHasMore: section.bank.areasHasMore,
		icon: section.bank.icon ?? undefined,
	});

	fieldOfAreas.push({
		_key: section.collection._id,
		title: section.collection.title ?? '',
		href: internalLinkToHref(section.collection._type, section.collection.slug ?? ''),
		areas: section.collection.areas?.map((area) => ({
			_key: area._id,
			title: area.title ?? '',
			href: internalLinkToHref(area._type, area.slug ?? ''),
		})),
		areasHasMore: section.collection.areasHasMore,
		icon: section.collection.icon ?? undefined,
	});

	return fieldOfAreas;
};
